import styles from 'css/src/components/pricing/pricing.module.scss';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { format, parseISO } from 'date-fns';
import type { Plan } from 'js/src/client';
import { ApiService, Urls } from 'js/src/client';
import JumboCta from 'js/src/components/common/JumboCta';
import LogoImages from 'js/src/components/common/LogoImages';
import { Toast } from 'js/src/components/common/Toast';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import BillingPeriodToggle from 'js/src/components/pricing/BillingPeriodToggle';
import ComparisonTable from 'js/src/components/pricing/ComparisonTable';
import NotificationBlock from 'js/src/components/pricing/NotificationBlock';
import PlanBlockList from 'js/src/components/pricing/PlanBlockList';
import PlanSwitchErrorHandlerModal from 'js/src/components/pricing/PlanSwitchErrorHandlerModal';
import SubscriptionChangeModal from 'js/src/components/pricing/SubscriptionChangeModal';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import { ANALYTICS_QS, AUTO_UPGRADE_QS, UPGRADE_STATUS } from 'js/src/components/pricing/types';
import CompanyInfoSection from 'js/src/components/public-pages/common-sections/CompanyInfoSection';
import { logoImages } from 'js/src/components/public-pages/homepage-data';
import {
    handlePlanSwitchError as handlePlanSwitchErrorThunk,
    reloadPageSuccess,
    retrySubscriptionChange as retrySubscriptionChangeThunk,
    switchToPlan as switchToPlanThunk,
    togglePlanSwitchErrorModal as togglePlanSwitchErrorModalThunk,
    updateSubscription as updateSubscriptionThunk,
    waitForPlanToUpdate,
} from 'js/src/data/pricing/pricing-operations';
import {
    setAnnualPlans,
    setChangingSubscription,
    setCouponStatus,
    setMonthlyPlans,
    setSelectedMonthly,
    setShowSubscriptionChangeConfirmation,
    setSubscriptionDetails,
    setUpgradeStatus,
    setUserHasPaymentMethodSetup,
} from 'js/src/data/pricing/pricing-slice';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { gaSendPlanPurchase } from 'js/src/libs/ga-ecommerce';
import { clearLastUpsell, LAST_UPSELL_QS_KEY } from 'js/src/libs/last-upsell';
import { getQueryParam } from 'js/src/libs/querystring';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';
import type { RootState } from 'js/src/libs/RootState';
import { getPlanDisplayName } from 'js/src/libs/server-utils';

const placeholderPlans = Array(4).fill(null);

const STAR_PATH =
    'M8 1l1.76 4.57 4.9.27-3.8 3.09 1.27 4.64L8 11.5 3.87 13.57l1.27-4.64-3.8-3.09 4.9-.27z';

const isFreePlan = (plan: Plan): boolean => {
    return plan.price === 0 || plan.name === '';
};

const isPaidPlan = (plan: Plan): boolean => {
    return !isFreePlan(plan);
};

const Pricing = React.memo(function Pricing() {
    const dispatch = useDispatch();
    const [openAccordionItem, setOpenAccordionItem] = React.useState<string | null>(null);

    // Redux state
    const selectedMonthly = useSelector((state: RootState) => state.pricing.selectedMonthly);
    const monthlyPlans = useSelector((state: RootState) => state.pricing.monthlyPlans);
    const annualPlans = useSelector((state: RootState) => state.pricing.annualPlans);
    const couponStatus = useSelector((state: RootState) => state.pricing.couponStatus);
    const showSubscriptionChangeConfirmation = useSelector(
        (state: RootState) => state.pricing.showSubscriptionChangeConfirmation,
    );
    const changingSubscription = useSelector(
        (state: RootState) => state.pricing.changingSubscription,
    );
    const upgradeStatus = useSelector((state: RootState) => state.pricing.upgradeStatus);
    const subscriptionDetails = useSelector(
        (state: RootState) => state.pricing.subscriptionDetails,
    );
    const confirmationModalData = useSelector(
        (state: RootState) => state.pricing.confirmationModalData,
    );
    const showPlanSwitchErrorModal = useSelector(
        (state: RootState) => state.pricing.showPlanSwitchErrorModal,
    );
    const planSwitchErrorInfo = useSelector(
        (state: RootState) => state.pricing.planSwitchErrorInfo,
    );

    const showAnnualPromo = Urls.SPECIAL_PRICING === window.location.pathname;

    const gaIntervalRef = React.useRef<number | null>(null);
    const gtagIntervalRef = React.useRef<number | null>(null);

    const sendGoogleAnalyticsPurchase = React.useCallback(
        (value: number, plan_name: string, last_upsell?: string) => {
            if (typeof ga === 'undefined') {
                return;
            }

            if (gaIntervalRef.current !== null) {
                window.clearInterval(gaIntervalRef.current);
                gaIntervalRef.current = null;
            }

            gaSendPlanPurchase(plan_name, value, last_upsell);
        },
        [],
    );

    const sendGoogleAdsPurchase = React.useCallback(
        (value: number, plan_name: string, last_upsell?: string) => {
            if (typeof gtag === 'undefined') {
                return;
            }

            if (gtagIntervalRef.current !== null) {
                window.clearInterval(gtagIntervalRef.current);
                gtagIntervalRef.current = null;
            }

            gtag('event', 'conversion', {
                send_to: 'AW-835353444/eImVCPOo4IIBEOT2qY4D',
                value: value,
                currency: 'USD',
                plan_name: plan_name,
                last_upsell: last_upsell,
            });

            gtag('event', 'conversion', {
                send_to: 'AW-372587568/3JJyCMDehpICELD41LEB',
                value: value,
                currency: 'USD',
                plan_name: plan_name,
                last_upsell: last_upsell,
            });
        },
        [],
    );

    const recordAnalytics = React.useCallback(() => {
        const analyticsValue = getQueryParam(ANALYTICS_QS.KEY);
        if (analyticsValue !== undefined && analyticsValue !== ANALYTICS_QS.DONE) {
            // This handles our 'thank you' page after checkout, to record analytics.
            // The page is loaded with the new plan the user just upgraded to, while the old
            // plan info is passed in the query string.

            const last_upsell = getQueryParam(LAST_UPSELL_QS_KEY);

            // send google conversion event if we've upgraded from the free plan
            const old_plan_price = parseInt(analyticsValue);
            if (old_plan_price === 0 && !!SERVER.USER) {
                // New plan data
                let dollars_per_month = SERVER.USER.plan_price;
                const plan_name = SERVER.USER.plan;
                const is_annual = SERVER.USER.plan_billing_cycle === 'annual';

                // calculate dollars per month
                if (is_annual) {
                    dollars_per_month /= 12.0;
                }

                // We use setInterval to send these because loading the third party scripts
                // happens asynchronously and may not be ready when this component is mounted
                // send google analytics
                gaIntervalRef.current = window.setInterval(() => {
                    sendGoogleAnalyticsPurchase(dollars_per_month, plan_name, last_upsell);
                }, 200);

                // send google ads
                gtagIntervalRef.current = window.setInterval(() => {
                    sendGoogleAdsPurchase(dollars_per_month, plan_name, last_upsell);
                }, 200);
            }

            // The purchase this upsell led to is done — don't attribute a later one to it.
            clearLastUpsell();

            // update the location so if the user refreshes the page, we won't report again
            const new_location = QueryStringManipulator.updateQueryStringParameter(
                window.location.toString(),
                ANALYTICS_QS.KEY,
                ANALYTICS_QS.DONE,
            );
            window.history.replaceState({ path: new_location }, '', new_location);
        }
    }, [sendGoogleAnalyticsPurchase, sendGoogleAdsPurchase]);

    const loadPlans = React.useCallback(() => {
        ApiService.pricingPlansRetrieve({ query: { coupon: getQueryParam('cpn')! } })
            .then((response) => {
                dispatch(setMonthlyPlans(response.data.monthly_plans));
                dispatch(setAnnualPlans(response.data.annual_plans));
                dispatch(setCouponStatus(response.data.coupon_status));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading pricing plans. Please refresh page.');
            });
    }, [dispatch]);

    const loadPaymentMethodStatus = React.useCallback(() => {
        // don't load for logged out users
        if (SERVER.USER === null) {
            return;
        }

        ApiService.pricingPaymentMethodStatusRetrieve()
            .then((response) => {
                dispatch(setUserHasPaymentMethodSetup(response.data.has_payment_configured));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading payment method status. Please refresh page.');
            });
    }, [dispatch]);

    const loadSubscriptionDetails = React.useCallback(() => {
        if (!SERVER.USER) {
            // not logged in, don't try loading the subscription
            return;
        }
        ApiService.pricingSubscriptionRetrieve()
            .then((response) => {
                dispatch(setSubscriptionDetails(response.data));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading subscription details. Please refresh page.');
            });
    }, [dispatch]);

    React.useEffect(() => {
        recordAnalytics();
        loadPlans();
        loadPaymentMethodStatus();
        loadSubscriptionDetails();
    }, [loadPaymentMethodStatus, loadPlans, loadSubscriptionDetails, recordAnalytics]);

    React.useEffect(() => {
        let upgradeStatusInitial = null;
        let changingSubscriptionInitial = false;
        if (SERVER.USER) {
            if (getQueryParam('success') === 'true') {
                upgradeStatusInitial = UPGRADE_STATUS.SUCCESS;
            } else if (getQueryParam('pm_updated') === 'true') {
                const selected_plan = getQueryParam('selected_plan');
                if (SERVER.USER.plan === selected_plan) {
                    upgradeStatusInitial = UPGRADE_STATUS.SUCCESS;
                } else {
                    changingSubscriptionInitial = true;
                }
            }
        }

        dispatch(setChangingSubscription(changingSubscriptionInitial));
        dispatch(setUpgradeStatus(upgradeStatusInitial));

        if (getQueryParam('pm_updated') === 'true' && changingSubscriptionInitial) {
            const selected_plan = getQueryParam('selected_plan');
            dispatch(waitForPlanToUpdate(selected_plan!));
        }
    }, [dispatch]);

    React.useEffect(() => {
        // Use existing account billing cycle (if any) - free users default to annual.
        if (subscriptionDetails?.is_monthly !== undefined && subscriptionDetails.price > 0) {
            dispatch(setSelectedMonthly(subscriptionDetails.is_monthly));
        }
    }, [subscriptionDetails?.is_monthly, subscriptionDetails?.price, dispatch]);

    const scrollToPlans = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const elem = document.getElementById('feature-comparison');
        if (!elem) throw new Error('Could not find element to scroll to');
        elem.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    const selectMonthly = React.useCallback(() => {
        dispatch(setSelectedMonthly(true));
    }, [dispatch]);

    const selectYearly = React.useCallback(() => {
        dispatch(setSelectedMonthly(false));
    }, [dispatch]);

    const renewSubscription = React.useCallback(() => {
        analytics.track('Subscription renewed');
        dispatch(setChangingSubscription(true));
        ApiService.pricingRenewSubscriptionCreate()
            .then((response) => {
                reloadPageSuccess();
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error(
                    'We could not automatically renew your subscription, please contact us at support@lumen5.com',
                );
                dispatch(setChangingSubscription(false));
            });
    }, [dispatch]);

    const switchToPlan = React.useCallback(
        (plan: Plan) => {
            dispatch(switchToPlanThunk(plan));
        },
        [dispatch],
    );

    const retrySubscriptionChange = React.useCallback(() => {
        dispatch(retrySubscriptionChangeThunk());
    }, [dispatch]);

    const updateSubscription = React.useCallback(
        (email: string, plan: Plan) => {
            dispatch(updateSubscriptionThunk(email, plan));
        },
        [dispatch],
    );

    const closeSubscriptionChangeModal = React.useCallback(() => {
        dispatch(setShowSubscriptionChangeConfirmation(false));
    }, [dispatch]);

    const handlePlanSwitchError = React.useCallback(
        (errorInfo: PlanSwitchErrorInfo) => {
            dispatch(handlePlanSwitchErrorThunk(errorInfo));
        },
        [dispatch],
    );

    const togglePlanSwitchErrorModal = React.useCallback(
        (visibilityFlag: boolean) => {
            dispatch(togglePlanSwitchErrorModalThunk(visibilityFlag));
        },
        [dispatch],
    );

    // When an in-app upsell sends a paid user here with `?auto_upgrade=<plan_name>`, trigger
    // the switch to that plan automatically once plans have loaded — so the upgrade happens on
    // the pricing page (with its full error handling) without an extra click from the user.
    const autoUpgradeTriggered = React.useRef(false);
    React.useEffect(() => {
        if (autoUpgradeTriggered.current) {
            return;
        }
        const autoUpgradePlanName = getQueryParam(AUTO_UPGRADE_QS.KEY);
        if (!autoUpgradePlanName) {
            return;
        }
        const planToUpgrade = [...monthlyPlans, ...annualPlans].find(
            (plan) => plan.name === autoUpgradePlanName,
        );
        if (planToUpgrade) {
            autoUpgradeTriggered.current = true;
            switchToPlan(planToUpgrade);
        }
    }, [monthlyPlans, annualPlans, switchToPlan]);

    let loadingNode = null;
    let autoRenewalNode = null;
    let pausedSubscriptionNode = null;
    if (changingSubscription) {
        loadingNode = (
            <div className={styles['loadingOverlay']}>
                <p>Loading...</p>
            </div>
        );
    }
    const planDisplayName = getPlanDisplayName();
    if (subscriptionDetails?.is_paused) {
        const pause_resumes_at = subscriptionDetails.pause_resumes_at;
        let resume_text = '';
        if (pause_resumes_at) {
            const resumeDate =
                typeof pause_resumes_at === 'string'
                    ? parseISO(pause_resumes_at)
                    : new Date(pause_resumes_at);
            resume_text = ` It will resume on ${format(resumeDate, 'MMM d, yyyy')}.`;
        }
        pausedSubscriptionNode = (
            <div className="col-md-12 my-l5-8">
                <Callout
                    msgType={CALLOUT_TYPES.ERROR}
                    title={`Your ${planDisplayName} subscription is currently paused.`}
                    text={resume_text}
                    ctaText="Manage subscription"
                    onCtaClick={() => {
                        window.location.href = Urls.BILLING_SETTINGS;
                    }}
                />
            </div>
        );
    } else if (subscriptionDetails?.cancel_at_period_end) {
        const next_billing_date = subscriptionDetails.next_billing_date;
        if (next_billing_date === null) {
            throw new Error('next_billing_date is null');
        }
        const nextBillingDate =
            typeof next_billing_date === 'string'
                ? parseISO(next_billing_date)
                : new Date(next_billing_date);
        const next_billing_text = format(nextBillingDate, 'MMM d, yyyy');
        autoRenewalNode = (
            <div className="col-md-12 my-l5-8">
                <Callout
                    msgType={CALLOUT_TYPES.ERROR}
                    title={`Your ${planDisplayName} subscription is set to not renew and you will be downgraded on ${next_billing_text}.`}
                    ctaText="Turn on auto-renewal"
                    onCtaClick={renewSubscription}
                />
            </div>
        );
    }

    const isCouponValid = couponStatus?.valid;
    const showBillingToggle = !isCouponValid && !showAnnualPromo;

    const showMonthlyPlans = (selectedMonthly || isCouponValid) && !showAnnualPromo;

    const sourcePlans = showMonthlyPlans ? monthlyPlans : annualPlans;
    const communityPlans = sourcePlans.filter(isFreePlan);
    const paidPlans = sourcePlans
        .filter(isPaidPlan)
        .filter((plan) => !plan.hide_in_public_pricing_page);
    const comparisonPlans = [...communityPlans, ...paidPlans];

    return (
        <div className={styles['pricing-page-container']}>
            <div className="container">
                {/* section 2 - pricing subheader */}
                <div
                    className={`row justify-content-center align-items-center ${styles['section-2']}`}
                >
                    <div className="col-md-8">
                        <h1 className={styles['pricing-heading']}>
                            Pricing that scales with your{' '}
                            <span className={styles['pricing-heading-accent']}>video strategy</span>
                        </h1>
                        <p className={styles['pricing-subheading']}>
                            Designed for business use. Turn blog posts, scripts, and ideas into
                            studio-quality video. No video editing experience required.
                        </p>
                    </div>
                </div>
            </div>
            <div className="container">
                {/* section 3 - billing period toggle */}
                {showBillingToggle && (
                    <div className={`row ${styles['section-3']}`}>
                        <div className="col d-flex flex-column justify-content-center align-items-center">
                            <div
                                className={`d-flex justify-content-center ${styles['social-proof-rating-wrapper']}`}
                            >
                                <div className={styles['social-proof-rating']}>
                                    <svg
                                        width="72"
                                        height="13"
                                        viewBox="0 0 90 16"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                    >
                                        <defs>
                                            <clipPath id="pricing-partial-star">
                                                <rect x="0" y="0" width="8" height="16" />
                                            </clipPath>
                                        </defs>
                                        {[0, 18, 36, 54].map((x) => (
                                            <path
                                                key={x}
                                                transform={`translate(${x},0)`}
                                                d={STAR_PATH}
                                                fill="#f5a623"
                                            />
                                        ))}
                                        <path
                                            transform="translate(72,0)"
                                            d={STAR_PATH}
                                            fill="#e0e0e0"
                                        />
                                        <g
                                            transform="translate(72,0)"
                                            clipPath="url(#pricing-partial-star)"
                                        >
                                            <path d={STAR_PATH} fill="#f5a623" />
                                        </g>
                                    </svg>
                                    <span className={styles['social-proof-score']}>4.6 / 5</span>
                                    <span className={styles['social-proof-meta']}>
                                        on G2 &amp; Capterra · 200+ reviews
                                    </span>
                                </div>
                            </div>
                            <BillingPeriodToggle
                                monthly={selectedMonthly}
                                selectMonthly={selectMonthly}
                                selectYearly={selectYearly}
                            />
                            <div className={styles['trust-badges']}>
                                {[
                                    'Start free, upgrade anytime',
                                    'Cancel anytime, no strings attached',
                                    'Your videos, yours to keep',
                                ].map((label) => (
                                    <span key={label}>
                                        <svg
                                            className={styles['trust-check']}
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M2.5 8.5L5.5 11.5L13.5 4.5"
                                                stroke="currentColor"
                                                strokeWidth="1.75"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                <div className="row justify-content-center align-items-center ">
                    <div className="col-8">
                        <div className="d-flex flex-column align-items-center">
                            {loadingNode}
                            {pausedSubscriptionNode}
                            {autoRenewalNode}
                            <NotificationBlock
                                couponStatus={couponStatus}
                                upgradeStatus={upgradeStatus ?? undefined}
                                planName={planDisplayName}
                                retryUpgradeWithNewCard={retrySubscriptionChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* section 4 - plans */}
            <div className="container">
                <div
                    className={`d-flex flex-sm-row flex-wrap justify-content-center align-items-stretch ${styles['section-4']}`}
                >
                    <PlanBlockList
                        plans={paidPlans.length ? paidPlans : placeholderPlans}
                        switchToPlan={switchToPlan}
                        handlePlanSwitchError={handlePlanSwitchError}
                        scrollToPlans={scrollToPlans}
                    />
                    <PlanBlockList
                        plans={communityPlans}
                        switchToPlan={switchToPlan}
                        handlePlanSwitchError={handlePlanSwitchError}
                        scrollToPlans={scrollToPlans}
                    />
                </div>
            </div>

            {/* section 7 - why teams choose lumen5 */}
            <div className={styles['value-prop-section']}>
                <div className="container">
                    <p className={styles['value-prop-eyebrow']}>Why teams choose Lumen5</p>
                    <h2 className={styles['value-prop-heading']}>
                        One subscription replaces your whole video stack
                    </h2>
                    <p className={styles['value-prop-subheading']}>
                        Your team can own video end to end. No agency, no contractors, no extra
                        tools to stitch together. Powered by AI that respects your brand.
                    </p>
                    <div className={`row g-4 mt-l5-12 ${styles['value-prop-cards']}`}>
                        <div className="col-lg-4 col-md-12">
                            <div className={styles['value-prop-card']}>
                                <div className={styles['value-prop-icon']}>
                                    <span className="material-symbols-outlined">timer</span>
                                </div>
                                <h3 className={styles['value-prop-card-title']}>
                                    Create in minutes
                                </h3>
                                <p className={styles['value-prop-card-desc']}>
                                    Start with a link, script or document and let AI build the first
                                    cut.
                                </p>
                                <ul className={styles['value-prop-list']}>
                                    <li>Repurpose content into video</li>
                                    <li>Make short clips from long videos</li>
                                    <li>Generate animated explainer videos</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12">
                            <div className={styles['value-prop-card']}>
                                <div className={styles['value-prop-icon']}>
                                    <span className="material-symbols-outlined">perm_media</span>
                                </div>
                                <h3 className={styles['value-prop-card-title']}>Media included</h3>
                                <p className={styles['value-prop-card-desc']}>
                                    Millions of commercially licensed stock assets and AI
                                    generation, built in.
                                </p>
                                <ul className={styles['value-prop-list']}>
                                    <li>Millions of stock photos &amp; video clips</li>
                                    <li>AI image &amp; video generation</li>
                                    <li>1000+ premium AI voices</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12">
                            <div className={styles['value-prop-card']}>
                                <div className={styles['value-prop-icon']}>
                                    <span className="material-symbols-outlined">palette</span>
                                </div>
                                <h3 className={styles['value-prop-card-title']}>Stay on brand</h3>
                                <p className={styles['value-prop-card-desc']}>
                                    Customize logos, colors and fonts so every video looks like you.
                                </p>
                                <ul className={styles['value-prop-list']}>
                                    <li>Brand kits &amp; video blueprints</li>
                                    <li>Custom watermarks, colors &amp; fonts</li>
                                    <li>100+ customizable templates</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                {/* section 6 - social proof */}
                <div className="mt-l5-48">
                    <div className={styles['social-proof']}>
                        <p className={styles['social-proof-eyebrow']}>Trusted by teams at</p>
                    </div>
                    <LogoImages logos={logoImages} />
                </div>
            </div>

            {/* section 8 - comparison table */}
            <div id="feature-comparison" className="container py-l5-48 mb-l5-48 mt-l5-12">
                <h3 className="text-center mb-l5-48">Compare every feature</h3>
                <ComparisonTable
                    plans={comparisonPlans}
                    switchToPlan={switchToPlan}
                    isAnnualPromo={showAnnualPromo}
                    handlePlanSwitchError={handlePlanSwitchError}
                />
            </div>

            {/* section 9 - case study carousel */}
            <div className="container py-l5-48 my-l5-48">
                <CompanyInfoSection />
            </div>

            {/* section 10 - faqs */}
            <div className="container pb-l5-48">
                <div className="row mb-l5-16">
                    <div className="col-md-12 my-l5-48">
                        <h3 className="text-center my-l5-24">Frequently asked questions</h3>
                    </div>
                </div>
                <div className="row justify-content-center">
                    <div className="col-lg-8 col-md-10">
                        <div className={styles['faq-list']}>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item1' ? null : 'item1',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item1'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Do I need a credit card to get started?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item1' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item1' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        No. You can sign up and start creating videos for free
                                        without entering any payment details. A credit card is only
                                        required when you choose to upgrade to a paid plan.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item2' ? null : 'item2',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item2'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Can I cancel my subscription at any time?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item2' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item2' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        Yes. You can cancel your subscription at any time from your
                                        account settings. You will retain access to your paid plan
                                        features until the end of your current billing period, and
                                        all videos you have created remain yours to keep.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item3' ? null : 'item3',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item3'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        What payment methods do you accept?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item3' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item3' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        We accept all major credit cards including Visa, Mastercard,
                                        and American Express. Team plan customers can also arrange
                                        annual invoicing. Reach out to our sales team to learn more.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item4' ? null : 'item4',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item4'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Which Lumen5 plan is best for my team?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item4' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item4' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        It depends on your goals. The Basic and Starter plans are
                                        ideal for individual creators who need simple, fast video
                                        creation. Pro is best for small businesses that require
                                        advanced branding, premium media, and more customization.
                                        The Team plan is built for organizations that need custom
                                        templates, collaboration features, workflow controls,
                                        translations, SSO, and enterprise grade support.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item5' ? null : 'item5',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item5'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        What makes the Team plan different from the Pro plan?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item5' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item5' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        The Team plan includes everything in Pro plus enterprise
                                        capabilities such as custom branded templates, bespoke scene
                                        designs, workspace and user management, translation tools,
                                        SSO, approval workflows, DAM integrations, and a dedicated
                                        customer success manager. It is designed for companies
                                        scaling video creation across multiple teams or locations.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item6' ? null : 'item6',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item6'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        What AI features are included in each plan?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item6' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item6' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        All paid plans include AI voiceover (using 1000+ lifelike
                                        voices), automated captions, premium AI image generation,
                                        and AI Script Composer. Voiceover minutes and animation
                                        minutes vary by plan. Voice cloning, where you record a
                                        short sample and use it to narrate any video, is available
                                        on Starter and above, with more cloned voices on higher
                                        tiers. Video translation is available on all paid plans,
                                        with unlimited translations on the Team plan.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item7' ? null : 'item7',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item7'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Does Lumen5 support brand consistency across video content?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item7' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item7' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        Yes. Custom colors and the font library are included on
                                        every plan, and Starter and Pro add brand kits and font
                                        uploads. The Team plan adds bespoke branded templates,
                                        custom transitions, and support from the Lumen5 Creative
                                        Services team so every exported video stays aligned with
                                        your brand identity across teams and regions.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item8' ? null : 'item8',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item8'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        How does video translation work on the higher tier plans?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item8' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item8' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        Translation credits allow you to create localized versions
                                        of your videos. Pro users receive sixty translations per
                                        year, while Team customers receive unlimited translations.
                                        This is especially helpful for global marketing, internal
                                        communications, and learning and development teams that need
                                        multi language content.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item9' ? null : 'item9',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item9'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Will my videos remain accessible if I downgrade or cancel my
                                        plan?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item9' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item9' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        Yes. All videos you create are yours to keep permanently.
                                        You can download and reuse them even if you cancel your
                                        plan. You will only lose access to premium features
                                        associated with higher tier subscriptions.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="my-l5-48">
                <JumboCta
                    header="Join over 5,000,000 companies using Lumen5 to tell their stories."
                    body="Lumen5 helps marketers, publishers, and brands create video content with AI, without the hassle or technical expertise."
                />
            </div>
            <SubscriptionChangeModal
                showing={showSubscriptionChangeConfirmation}
                subscription_data={confirmationModalData}
                coupon_data={couponStatus}
                update_subscription={updateSubscription}
                close_subscription_change_modal={closeSubscriptionChangeModal}
            />
            <PlanSwitchErrorHandlerModal
                showing={showPlanSwitchErrorModal}
                errorInfo={planSwitchErrorInfo}
                onModalClose={togglePlanSwitchErrorModal}
            />
        </div>
    );
});

export default Pricing;
